import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { availableColumns, CSVColumn } from "../../../helpers/csvExport";

interface ExportCSVModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    onExport: (selectedColumns: string[], includeSkills: boolean) => void;
    isShipping?: boolean;
    candidateCount?: number;
}

export default function ExportCSVModal({
    open,
    setOpen,
    onExport,
    isShipping = false,
    candidateCount,
}: ExportCSVModalProps) {
    const [selectedColumns, setSelectedColumns] = useState<string[]>([
        "firstName",
        "lastName",
        "email",
        "age",
        "workLocation1",
        "experienceInMonths",
        "combinedGrade",
        "score",
    ]);
    const [includeSkills, setIncludeSkills] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    // Group columns by category
    const columnGroups = {
        basic: ["firstName", "lastName", "email", "age", "workLocation1", "workLocation2"],
        shipping: ["shippingDepartment", "onBoardExperienceInMonths", "onBoardExperiencePercentileMonths", "totalMaritimeAcademyEducationGrade", "totalOnBoardExperienceGrade"],
        experience: ["experienceInMonths", "experiencePercentileMonths", "yearsOfExperience", "totalExperienceGrade"],
        education: ["totalEducationGrade", "expectedGraduationDate"],
        grades: ["combinedGrade", "combinedGradePercentile", "combinedPercentileBasedAge", "combinePercentileBasedEXP"],
        assessment: ["score", "scorePercentile"],
        other: ["totalGapMonths", "gappercentile", "noticePeriod", "latestStatusDate"],
        cvErrors: ["cvMismatchError", "isEducationDetailsMismatch", "isEducationPeriodMismatch", "isExperienceDetailsMismatch", "isExperiencePeriodMismatch"],
    };

    const handleToggleColumn = (columnKey: string) => {
        setSelectedColumns((prev) =>
            prev.includes(columnKey)
                ? prev.filter((k) => k !== columnKey)
                : [...prev, columnKey]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(availableColumns.map((col) => col.key));
        }
        setSelectAll(!selectAll);
    };

    const handleSelectGroup = (groupKeys: string[]) => {
        const allSelected = groupKeys.every((key) => selectedColumns.includes(key));
        if (allSelected) {
            setSelectedColumns((prev) => prev.filter((k) => !groupKeys.includes(k)));
        } else {
            setSelectedColumns((prev) => [...new Set([...prev, ...groupKeys])]);
        }
    };

    const handleExport = () => {
        onExport(selectedColumns, includeSkills);
        setOpen(false);
    };

    useEffect(() => {
        setSelectAll(selectedColumns.length === availableColumns.length);
    }, [selectedColumns]);

    const renderColumnGroup = (title: string, keys: string[]) => {
        const columns = availableColumns.filter((col) => keys.includes(col.key));
        const allSelected = keys.every((key) => selectedColumns.includes(key));

        return (
            <div className="mb-4">
                <div className="flex items-center mb-2">
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => handleSelectGroup(keys)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label className="ml-2 text-sm font-semibold text-gray-900 dark:text-gray-200">
                        {title}
                    </label>
                </div>
                <div className="ml-6 grid grid-cols-2 gap-2">
                    {columns.map((col) => (
                        <div key={col.key} className="flex items-center">
                            <input
                                type="checkbox"
                                checked={selectedColumns.includes(col.key)}
                                onChange={() => handleToggleColumn(col.key)}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                            <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                {col.label}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbglight px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white dark:bg-darkbglight text-gray-400 hover:text-gray-500"
                                        onClick={() => setOpen(false)}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-200 mb-4"
                                        >
                                            Export Candidates to CSV
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                                {candidateCount !== undefined && (
                                                    <span className="font-semibold text-gray-900 dark:text-gray-200">
                                                        {candidateCount} selected {candidateCount === 1 ? "candidate" : "candidates"} will be exported.{" "}
                                                    </span>
                                                )}
                                                Select the columns you want to include in the CSV export.
                                            </p>

                                            {/* Select All */}
                                            <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-600">
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectAll}
                                                        onChange={handleSelectAll}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                    />
                                                    <label className="ml-2 text-sm font-bold text-gray-900 dark:text-gray-200">
                                                        Select All Columns
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Column Groups */}
                                            <div className="max-h-96 overflow-y-auto">
                                                {renderColumnGroup("Basic Information", columnGroups.basic)}
                                                {isShipping && renderColumnGroup("Shipping Specific", columnGroups.shipping)}
                                                {renderColumnGroup("Experience", columnGroups.experience)}
                                                {renderColumnGroup("Education", columnGroups.education)}
                                                {renderColumnGroup("Grades & Percentiles", columnGroups.grades)}
                                                {renderColumnGroup("Assessment", columnGroups.assessment)}
                                                {renderColumnGroup("Other", columnGroups.other)}
                                                {renderColumnGroup("CV Validation Errors", columnGroups.cvErrors)}
                                            </div>

                                            {/* Include Skills */}
                                            {/* <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={includeSkills}
                                                        onChange={(e) => setIncludeSkills(e.target.checked)}
                                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                    />
                                                    <label className="ml-2 text-sm font-semibold text-gray-900 dark:text-gray-200">
                                                        Include Skill Scores & Percentiles
                                                    </label>
                                                </div>
                                                <p className="ml-6 text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                    This will add columns for each skill with their scores and percentiles
                                                </p>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={handleExport}
                                        disabled={selectedColumns.length === 0}
                                    >
                                        {candidateCount !== undefined
                                            ? `Export ${candidateCount} ${candidateCount === 1 ? "candidate" : "candidates"} (${selectedColumns.length} columns)`
                                            : `Export (${selectedColumns.length} columns)`}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-darkbg px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto"
                                        onClick={() => setOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
